// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ApiHandlerDiscreteParameter, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { HasOnError, HasOnSuccess } from "@insite/client-framework/HandlerCreator";
import { getAccount } from "@insite/client-framework/Services/AccountService";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { Cart, getCart, GetCartApiParameter } from "@insite/client-framework/Services/CartService";
import {
    getSession as getSessionApi,
    Session,
    UpdateSessionApiParameter,
} from "@insite/client-framework/Services/SessionService";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { chain, NavigateToReturnUrl } from "@insite/client-framework/Store/Context/Handlers/ChangeCustomerContext";
import { getQueryStrings } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getHomePageUrl, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";

type HandlerType = ApiHandlerDiscreteParameter<
    {
        billToId?: string;
        shipToId?: string;
        fulfillmentMethod: string;
        pickUpWarehouse: WarehouseModel | null;
        isDefault?: boolean;
        returnUrl?: string;
        skipRedirect?: boolean;
    } & HasOnSuccess &
        HasOnError<string>,
    UpdateSessionApiParameter,
    Session,
    {
        cart: Cart;
    }
>;

export const XngNavigateToReturnUrl: HandlerType = async props => {
    if (props.parameter.skipRedirect) {
        return;
    }

    const state = props.getState();
    const session = getSession(state);
    const homePageUrl = getPageLinkByPageType(state, "HomePage")?.url;
    let languageSpecificLandingPage = session.customLandingPage;
    if (homePageUrl && homePageUrl !== "/") {
        if (languageSpecificLandingPage && languageSpecificLandingPage.startsWith("/")) {
            languageSpecificLandingPage = homePageUrl.concat(session.customLandingPage);
        } else {
            languageSpecificLandingPage = homePageUrl.concat("/", session.customLandingPage);
        }
    }
    const defaultUrl = session.dashboardIsHomepage
        ? getPageLinkByPageType(state, "MyAccountPage")?.url
        : session.customLandingPage;

    const checkoutShippingUrl = getPageLinkByPageType(state, "CheckoutShippingPage")?.url;
    let returnUrl = props.parameter.returnUrl;
    const { userId } = getQueryStrings(state);
    const userSetupPage = getPageLinkByPageType(state, "UserSetupPage");
    const userSetupPageUrl = userSetupPage?.url;
    if (props.apiParameter.session.shipToId === "") {
        returnUrl = `${getPageLinkByPageType(state, "AddressesPage")?.url}?isNewShipTo=true`;
    } else if (returnUrl?.toLowerCase() === checkoutShippingUrl?.toLowerCase()) {
        const cartResult = await getCart({
            cartId: API_URL_CURRENT_FRAGMENT,
            expand: ["cartLines", "hiddenproducts"],
        } as GetCartApiParameter);
        const { canCheckOut, canBypassCheckoutAddress } = cartResult.cart;
        const checkoutReviewAndSubmitUrl = getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url;
        const cartUrl = getPageLinkByPageType(state, "CartPage")?.url;
        if (!canCheckOut || session.isRestrictedProductExistInCart) {
            returnUrl = cartUrl;
        } else if (canBypassCheckoutAddress) {
            returnUrl = checkoutReviewAndSubmitUrl;
        }
    } else if (
        returnUrl &&
        userSetupPageUrl &&
        returnUrl.toLocaleLowerCase().indexOf(userSetupPageUrl.toLocaleLowerCase()) > -1 &&
        userId
    ) {
        // If customer is not assigned to current user, return to UserAdministration page.
        try {
            await getAccount({ accountId: userId, expand: ["approvers", "roles"] });
        } catch (error) {
            returnUrl = getPageLinkByPageType(state, "UserListPage")?.url;
        }
    }

    // <xng-change>
    const returnUrlFinal = returnUrl?.toLowerCase()?.includes("/signin")
        ? session.dashboardIsHomepage
            ? getPageLinkByPageType(state, "MyAccountPage")?.url
            : "/"
        : returnUrl;
    window.location.href = defaultUrl || returnUrlFinal || getHomePageUrl(state);
    // <xng-change>
};

replaceInChain(chain, NavigateToReturnUrl, XngNavigateToReturnUrl);
